<script setup>
// Settings → Security dialog (docs/pwa/DESIGN.md §4): password confirmation, then for a PIN
// "choose" and "repeat" on the keypad. Adding a passkey only needs the password.
import axios from 'axios';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { extractApiError } from '../apiError';
import { t } from '../i18n';
import PinPad from './PinPad.vue';

const props = defineProps({
  // 'set-pin' | 'add-passkey'
  purpose: {
    type: String,
    required: true,
  },
  passwordConfirmed: {
    type: Boolean,
    default: false,
  },
});

// confirmed: password is fine (add-passkey continues in the parent); saved: PIN stored.
const emit = defineEmits(['close', 'confirmed', 'saved']);

const step = ref(props.passwordConfirmed ? 'choose' : 'password');
const password = ref('');
const firstPin = ref('');
const busy = ref(false);
const error = ref('');
const errorKey = ref(0);
const passwordInput = ref(null);
const pad = ref(null);

const title = computed(() => {
  if (step.value === 'password') {
    return t('security.confirmPassword');
  }
  return step.value === 'choose' ? t('security.choosePin') : t('security.repeatPin');
});

async function confirmPassword() {
  if (!password.value) {
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    await axios.post('/user/confirm-password', { password: password.value });
    password.value = '';
    if (props.purpose === 'set-pin') {
      step.value = 'choose';
    } else {
      emit('confirmed');
    }
  } catch (e) {
    error.value = e?.response?.status === 422 ? t('security.wrongPassword') : extractApiError(e, t('security.failed'));
  } finally {
    busy.value = false;
  }
}

async function onPin(pin) {
  error.value = '';
  if (step.value === 'choose') {
    firstPin.value = pin;
    step.value = 'repeat';
    await nextTick();
    pad.value?.clear();
    return;
  }

  if (pin !== firstPin.value) {
    firstPin.value = '';
    error.value = t('security.pinMismatch');
    errorKey.value += 1;
    step.value = 'choose';
    return;
  }

  busy.value = true;
  try {
    await axios.put('/user/pin', { pin });
    emit('saved', t('security.pinSaved'));
  } catch (e) {
    if (e?.response?.data?.reason === 'password_confirmation_required') {
      step.value = 'password';
      return;
    }
    error.value = extractApiError(e, t('security.failed'));
    errorKey.value += 1;
    step.value = 'choose';
  } finally {
    busy.value = false;
  }
}

function onKeydown(e) {
  if (e.key === 'Escape' && step.value === 'password') {
    emit('close');
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  passwordInput.value?.focus();
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      @click.self="emit('close')"
    >
      <div
        class="w-full rounded-t-3xl sm:max-w-sm bg-slate-900 p-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] shadow-2xl max-sm:max-h-[88dvh] max-sm:overflow-y-auto sm:rounded-xl sm:border sm:border-slate-700 sm:pb-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="security-dialog-title"
      >
        <h3
          id="security-dialog-title"
          class="text-base font-semibold text-slate-100"
          :class="step === 'password' ? '' : 'text-center'"
        >
          {{ title }}
        </h3>

        <form
          v-if="step === 'password'"
          class="mt-4 space-y-3"
          @submit.prevent="confirmPassword"
        >
          <input
            ref="passwordInput"
            v-model="password"
            type="password"
            autocomplete="current-password"
            :placeholder="t('auth.password')"
            class="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          >
          <p
            v-if="error"
            class="text-sm text-rose-400"
            role="alert"
          >
            {{ error }}
          </p>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded px-3 py-2 text-sm text-slate-300 hover:text-slate-100"
              @click="emit('close')"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="rounded bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
              :disabled="busy || !password"
            >
              {{ t('security.continue') }}
            </button>
          </div>
        </form>

        <template v-else>
          <PinPad
            ref="pad"
            class="mt-6"
            :disabled="busy"
            :error-key="errorKey"
            @complete="onPin"
          />
          <p
            class="mt-4 min-h-5 text-center text-sm text-rose-400"
            role="alert"
          >
            {{ error }}
          </p>
          <div class="mt-2 flex justify-center">
            <button
              type="button"
              class="rounded px-3 py-2 text-sm text-slate-400 hover:text-slate-200"
              @click="emit('close')"
            >
              {{ t('common.cancel') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>
